import React, { useCallback, useEffect, useRef, useState } from 'react';

import { type AnnotationPreviewPatch, applyAnnotationBoxPatch, applyStyleRun, type EffectiveTextStyle, remapStyleRuns, renderAnnotationTextRuns, resolveAnnotationStyleRuns, type StyledTextSegment, type StyleRunPatch, toStyledTextSegments, useSkuAnnotation } from '../../sku-annotations';
import type { SkuAnnotation, StyleRun } from '@rta/rta-blueprint-library';

export type BlueprintPageAnnotationTextProps = {
	annotation: SkuAnnotation;
	/** Every mutable field this component reads, flattened -- the annotation instance mutates in place, so React.memo has nothing else to compare */
	signature: string;
	isEditing: boolean;
	isSelected: boolean;
	/** Uncommitted whole-box patch from a picker drag; honoured for display only, never written to the model here */
	previewPatch: AnnotationPreviewPatch | null;
	onCommit: () => void;
};

type TextDraft = { text: string; runs: Array<StyleRun> };

type TextRange = { start: number; end: number };

const ZERO_WIDTH_SPACE = '​';

const toDraft = (annotation: SkuAnnotation): TextDraft => ({ text: annotation.text, runs: resolveAnnotationStyleRuns(annotation) });

const runsAreEqual = (first: Array<StyleRun>, second: Array<StyleRun>): boolean => JSON.stringify(first) === JSON.stringify(second);

type GetAnnotationTextStyleParams = {
	annotation: SkuAnnotation;
	previewPatch: AnnotationPreviewPatch | null;
};

/**
 * One style object for the read div, the edit-mode mirror div and the textarea -- the mirror only lines up
 * with the caret if all three agree on every metric. Blueprint-page annotations store raw page px, so no
 * font-size normalization applies here (that is the SKU-overhead surface's convention).
 */
const getAnnotationTextStyle = (params: GetAnnotationTextStyleParams): React.CSSProperties => {
	const { annotation, previewPatch } = params;
	const { color, fontWeight, fontStyle, fontFamily, fontSize } = previewPatch ?? {};

	return {
		width: '100%',
		height: '100%',
		margin: 0,
		padding: '2px',
		boxSizing: 'border-box',
		overflow: 'hidden',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
		textAlign: (annotation.textAlign || 'left') as React.CSSProperties['textAlign'],
		lineHeight: 'normal',
		letterSpacing: 'normal',
		fontSize: `${typeof fontSize === 'number' ? fontSize : annotation.fontSize}px`,
		color: color || annotation.textColor || annotation.color,
		fontFamily: `${fontFamily || annotation.fontFamily || 'Arial'}, sans-serif`,
		fontWeight: fontWeight || annotation.fontWeight || 'normal',
		fontStyle: fontStyle || annotation.fontStyle || 'normal'
	};
};

const getTextFrame = (annotation: SkuAnnotation): { x: number; y: number; width: number; height: number } => ({
	x: annotation.x + 2,
	y: annotation.y + 2,
	width: Math.max(0, annotation.width - 4),
	height: Math.max(0, annotation.height - 4)
});


/** Style-run overrides as SVG presentation attributes. `backgroundColor` (highlight) has no SVG text equivalent and is dropped. */
const toSvgRunAttrs = (style: EffectiveTextStyle | null): React.SVGProps<SVGTSpanElement> => {
	if (!style) return {};
	const { color, fontWeight, fontStyle, textDecoration, fontSize, fontFamily } = style;

	return {
		...(color ? { fill: color } : {}),
		...(fontWeight ? { fontWeight } : {}),
		...(fontStyle ? { fontStyle } : {}),
		...(textDecoration ? { textDecoration } : {}),
		...(typeof fontSize === 'number' ? { fontSize } : {}),
		...(fontFamily ? { fontFamily: `${fontFamily}, sans-serif` } : {})
	};
};

/** Splits styled segments across hard newlines, so each rendered line keeps its own per-run styling */
const toStyledLines = (text: string, runs: Array<StyleRun>): Array<Array<StyledTextSegment>> => {
	const lines: Array<Array<StyledTextSegment>> = [[]];
	toStyledTextSegments(text, runs).forEach((segment) => {
		segment.text.split('\n').forEach((part, index) => {
			if (index > 0) lines.push([]);
			if (part) lines[lines.length - 1].push({ text: part, style: segment.style });
		});
	});
	return lines;
};

type AnnotationSvgTextProps = {
	annotation: SkuAnnotation;
	previewPatch: AnnotationPreviewPatch | null;
	isSelected: boolean;
};

/**
 * Display-mode text renders as SVG `<text>`, not HTML in a `<foreignObject>`: html2canvas does not preserve
 * inline CSS colour inside a foreignObject when it rasterises for PDF/PPTX export, so the text landed black
 * on the exported page (GitHub #400). Edit mode keeps the foreignObject textarea.
 */
const AnnotationSvgText = (props: AnnotationSvgTextProps): React.ReactElement => {
	const { annotation, previewPatch, isSelected } = props;
	const { x, y, width, height } = getTextFrame(annotation);
	const { color, fontSize: previewFontSize, fontFamily, fontWeight, fontStyle } = previewPatch ?? {};

	const fontSize = typeof previewFontSize === 'number' ? previewFontSize : annotation.fontSize;
	const displayText = annotation.text || (isSelected ? '' : 'Double-click to edit...');
	const lines = toStyledLines(displayText, resolveAnnotationStyleRuns(annotation));
	const lineHeight = fontSize * 1.2;

	const align = annotation.textAlign || 'left';
	const textAnchor = align === 'center' ? 'middle' : align === 'right' ? 'end' : 'start';
	const textX = align === 'center' ? x + (width / 2) : align === 'right' ? x + width : x;

	const blockHeight = lines.length * lineHeight;
	const verticalAlign = annotation.verticalAlign || 'top';
	const blockTop = verticalAlign === 'middle' ? y + Math.max(0, (height - blockHeight) / 2)
		: verticalAlign === 'bottom' ? y + Math.max(0, height - blockHeight)
			: y;

	return (
		<text
			x={textX}
			y={blockTop + fontSize}
			textAnchor={textAnchor}
			fontSize={fontSize}
			fill={color || annotation.textColor || annotation.color}
			fontFamily={`${fontFamily || annotation.fontFamily || 'Arial'}, sans-serif`}
			fontWeight={fontWeight || annotation.fontWeight || 'normal'}
			fontStyle={fontStyle || annotation.fontStyle || 'normal'}
			style={{ pointerEvents: 'none', userSelect: 'none' }}
		>
			{lines.map((segments, lineIndex) => (
				<tspan key={lineIndex} x={textX} dy={lineIndex === 0 ? 0 : lineHeight}>
					{segments.length === 0
						? ' '
						: segments.map((segment, index) => <tspan key={index} {...toSvgRunAttrs(segment.style)}>{segment.text}</tspan>)}
				</tspan>
			))}
		</text>
	);
};

type AnnotationTextEditorProps = {
	annotation: SkuAnnotation;
	onCommit: () => void;
};

/**
 * The inline editor. Text and style runs live in local state and are committed to the model ONCE on save, so a
 * keystroke or a format click never fans a `sku_annotation:updated` event out through the whole page. The
 * textarea paints transparent text over a metric-identical mirror div, which is what makes per-range
 * formatting visible as the user applies it -- a textarea cannot style part of its own value.
 */
const AnnotationTextEditor = (props: AnnotationTextEditorProps): React.ReactElement => {
	const { annotation, onCommit } = props;
	const { setEditingAnnotationId, setActiveTextSelection, formatSelection, setFormatSelection, toolbarInteractingRef, applyStyleRef, liveStyleRunsRef, pendingSaveRef } = useSkuAnnotation();

	const textareaRef = useRef<HTMLTextAreaElement | null>(null);
	const [draft, setDraft] = useState<TextDraft>(() => toDraft(annotation));
	const draftRef = useRef<TextDraft>(draft);
	const formatRangeRef = useRef<TextRange | null>(null);
	const committedRef = useRef<boolean>(false);

	const writeDraft = useCallback((next: TextDraft): void => {
		draftRef.current = next;
		liveStyleRunsRef.current = { annotationId: annotation.id, runs: next.runs };
		setDraft(next);
	}, [annotation.id, liveStyleRunsRef]);

	useEffect(() => {
		liveStyleRunsRef.current = { annotationId: annotation.id, runs: draftRef.current.runs };
		return () => { if (liveStyleRunsRef.current?.annotationId === annotation.id) liveStyleRunsRef.current = null; };
	}, [annotation.id, liveStyleRunsRef]);

	useEffect(() => {
		formatRangeRef.current = formatSelection && formatSelection.annotationId === annotation.id
			? { start: formatSelection.start, end: formatSelection.end }
			: null;
	}, [formatSelection, annotation.id]);

	const commit = useCallback((): void => {
		if (committedRef.current) return;
		committedRef.current = true;
		const { text, runs } = draftRef.current;
		if (annotation.text !== text) annotation.text = text;
		// persisting the resolved runs into styleRuns and clearing colorRuns is the lazy per-record migration off the legacy field
		if (!runsAreEqual(resolveAnnotationStyleRuns(annotation), runs)) annotation.styleRuns = runs;
		if (annotation.colorRuns.length > 0) annotation.colorRuns = [];
		onCommit();
		setEditingAnnotationId(null);
		setActiveTextSelection(null);
		setFormatSelection(null);
	}, [annotation, onCommit, setEditingAnnotationId, setActiveTextSelection, setFormatSelection]);

	const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>): void => {
		const nextText = e.target.value;
		const { text: previousText, runs } = draftRef.current;
		writeDraft({ text: nextText, runs: remapStyleRuns({ runs, previousText, nextText }) });
		// Typing collapses the highlight, so the durable range goes with it -- keeping it would format characters the user can no longer see selected
		if (!formatRangeRef.current) return;
		formatRangeRef.current = null;
		setFormatSelection(null);
	}, [setFormatSelection, writeDraft]);

	// Never clears formatSelection on a collapsed range -- that range has to survive the toolbar stealing focus
	const handleSelectionChange = useCallback((e: React.SyntheticEvent<HTMLTextAreaElement>): void => {
		const { selectionStart, selectionEnd, value } = e.currentTarget;
		setActiveTextSelection(selectionStart !== selectionEnd ? { start: selectionStart, end: selectionEnd, text: value } : null);
		if (selectionStart === selectionEnd) return;
		formatRangeRef.current = { start: selectionStart, end: selectionEnd };
		setFormatSelection({ annotationId: annotation.id, start: selectionStart, end: selectionEnd });
	}, [annotation.id, setActiveTextSelection, setFormatSelection]);

	const handleBlur = useCallback((e: React.FocusEvent<HTMLTextAreaElement>): void => {
		if (toolbarInteractingRef.current) return;
		const next = e.relatedTarget as HTMLElement | null;
		if (next?.closest('[data-sku-toolbar]')) return;
		commit();
	}, [commit, toolbarInteractingRef]);

	const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
		if (e.key !== 'Escape' && (e.key !== 'Enter' || e.shiftKey)) return;
		e.preventDefault();
		commit();
	}, [commit]);

	// relatedTarget is null in some browsers and a toolbar popover may never hand focus back, so a mousedown that is neither the textarea nor toolbar chrome is the real end-of-session signal
	useEffect(() => {
		const handleDocumentMouseDown = (e: Event): void => {
			const target = e.target as HTMLElement | null;
			if (!target || target === textareaRef.current) return;
			if (target.closest('[data-sku-toolbar]') || target.closest('[data-annotation-context-menu]')) return;
			commit();
		};
		document.addEventListener('mousedown', handleDocumentMouseDown, true);
		return () => document.removeEventListener('mousedown', handleDocumentMouseDown, true);
	}, [commit]);

	// Anything that reads this annotation from the MODEL (saving it as a preset, click-outside teardown) flushes the draft through here first
	useEffect(() => {
		pendingSaveRef.current = commit;
		return () => { if (pendingSaveRef.current === commit) pendingSaveRef.current = null; };
	}, [commit, pendingSaveRef]);

	// The toolbar owns no runs; it hands the live editor a patch and the editor decides range vs whole box, then restores focus and the range so two formats can be chained on one selection
	useEffect(() => {
		const applyStyle = (patch: StyleRunPatch): void => {
			const textarea = textareaRef.current;
			// The textarea's own selection is the truth; the durable range is only the fallback for when the toolbar has taken focus
			const liveRange = textarea && textarea.selectionStart !== textarea.selectionEnd
				? { start: textarea.selectionStart, end: textarea.selectionEnd }
				: null;
			const candidate = liveRange ?? formatRangeRef.current;
			const range = candidate && candidate.end > candidate.start ? candidate : null;
			const { text, runs } = draftRef.current;
			const nextRuns = range
				? applyStyleRun({ runs, start: range.start, end: range.end, patch })
				: applyAnnotationBoxPatch({ annotation, patch, runs, textLength: text.length });
			writeDraft({ text, runs: nextRuns });
			if (range) {
				formatRangeRef.current = range;
				setFormatSelection({ annotationId: annotation.id, start: range.start, end: range.end });
			}
			// Never yank focus out of a control the user is still holding (a native colour picker closes if we do)
			if (!textarea || document.activeElement?.closest('[data-sku-toolbar]')) return;
			textarea.focus();
			if (range) textarea.setSelectionRange(range.start, range.end);
		};

		applyStyleRef.current = applyStyle;
		return () => { if (applyStyleRef.current === applyStyle) applyStyleRef.current = null; };
	}, [annotation, applyStyleRef, setFormatSelection, writeDraft]);

	const boxStyle = getAnnotationTextStyle({ annotation, previewPatch: null });
	const { text, runs } = draft;
	return (
		<div style={{ position: 'relative', width: '100%', height: '100%' }}>
			<div aria-hidden="true" style={{ ...boxStyle, position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
				{renderAnnotationTextRuns(text, runs)}{text.endsWith('\n') ? ZERO_WIDTH_SPACE : null}
			</div>
			<textarea
				ref={textareaRef}
				autoFocus
				value={text}
				onChange={handleChange}
				onSelect={handleSelectionChange}
				onKeyUp={handleSelectionChange}
				onMouseUp={handleSelectionChange}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				style={{ ...boxStyle, position: 'absolute', top: 0, left: 0, border: 'none', outline: 'none', background: 'transparent', resize: 'none', color: 'transparent', caretColor: annotation.textColor || annotation.color }}
			/>
		</div>
	);
};

const BlueprintPageAnnotationTextInner = (props: BlueprintPageAnnotationTextProps): React.ReactElement => {
	const { annotation, isEditing, isSelected, previewPatch, onCommit } = props;
	const { x, y, width, height } = getTextFrame(annotation);

	if (!isEditing) return <AnnotationSvgText annotation={annotation} previewPatch={previewPatch} isSelected={isSelected} />;

	return (
		<foreignObject x={x} y={y} width={width} height={height} style={{ pointerEvents: 'auto' }}>
			<AnnotationTextEditor annotation={annotation} onCommit={onCommit} />
		</foreignObject>
	);
};

export const BlueprintPageAnnotationText = React.memo(BlueprintPageAnnotationTextInner);
